// LazyImage.jsx
import React, { useState, useEffect, useRef } from 'react';
import './LazyImage.css';

const LazyImage = ({ src, alt, fallbackSrc, className, eager = false, loading, ...props }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(eager); // If eager, immediately set to true
  const [imgSrc, setImgSrc] = useState(eager ? src : null); // If eager, set src immediately
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const imgRef = useRef(null);

  // Determine native loading attribute
  const nativeLoading = loading || (eager ? 'eager' : 'lazy');

  // Set up the Intersection Observer to detect when the image is in viewport
  // Skip observer setup if eager loading is enabled
  useEffect(() => {
    // If eager loading, skip the observer entirely
    if (eager) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // When the image enters the viewport
        if (entries[0].isIntersecting) {
          setIsInView(true);
          // Once we've started loading, we can disconnect the observer
          observer.disconnect();
        }
      },
      // Increased rootMargin to 400px for earlier loading and smoother scrolling experience
      { rootMargin: '400px 0px', threshold: 0.01 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.disconnect();
      }
    };
  }, [eager]);

  // Once in view, set the actual image source
  useEffect(() => {
    if (isInView) {
      setImgSrc(src);
    }
  }, [isInView, src]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = (e) => {
    console.warn(`Failed to load image: ${src}`);
    setHasError(true);

    // Retry logic: try up to 2 times before falling back
    if (retryCount < 2) {
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setImgSrc(src); // Retry with original src
        setHasError(false);
      }, 1000 * (retryCount + 1)); // Exponential backoff: 1s, 2s
    } else if (fallbackSrc) {
      setImgSrc(fallbackSrc);
    }
  };

  return (
    <div
      ref={imgRef}
      className={`lazy-image-container ${isLoaded ? 'loaded' : 'loading'}`}
    >
      {/* Show placeholder/skeleton while loading */}
      {!isLoaded && <div className="lazy-image-placeholder" />}

      {/* Only render actual img tag when in view */}
      {isInView && imgSrc && (
        <img
          src={imgSrc}
          alt={alt || 'Product image'}
          className={`lazy-image ${className || ''} ${isLoaded ? 'visible' : 'hidden'}`}
          loading={nativeLoading}
          onLoad={handleLoad}
          onError={handleError}
          {...props}
        />
      )}
    </div>
  );
};

export default LazyImage;